import React, { useContext, useEffect, useState } from 'react';
import { Table, Button, Space, Card, Row, Col, Input } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { useMenu } from '../../hooks';

const { Search } = Input;

function Notifications() {
  const navigate = useNavigate();
  const { setHeaderComponent } = useContext(LayoutContext);
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuId, setMenuId] = useState(null);

  const { doFetch: fetchNotifications } = useFetch();
  const { menuData } = useMenu();

  useEffect(() => {
    setHeaderComponent('Notifications');

    return () => {
      setHeaderComponent(null);
    };
  }, [setHeaderComponent]);

  // Get menu_id from menu data
  useEffect(() => {
    if (menuData && menuData.id) {
      setMenuId(menuData.id);
    }
  }, [menuData]);

  // Load notifications when menuId is available
  useEffect(() => {
    if (menuId) {
      loadNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuId]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const filtered = notifications.filter(notification => {
        return (
          notification.title?.toLowerCase().includes(query) ||
          notification.description?.toLowerCase().includes(query)
        );
      });
      setFilteredNotifications(filtered);
    } else {
      setFilteredNotifications(notifications);
    }
  }, [searchQuery, notifications]);

  const loadNotifications = () => {
    if (!menuId) {
      console.warn('Menu ID not available yet, skipping notification load');
      return;
    }

    fetchNotifications({
      url: URLS.getNotifications({ menuId }),
      method: 'GET',
      onSuccess: (data) => {
        setNotifications(data || []);
        setFilteredNotifications(data || []);
      },
      onError: (error) => {
        console.error('Failed to load notifications:', error);
      },
    });
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || <span style={{ color: '#999' }}>No description</span>,
      ellipsis: true,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => (text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text) => (text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} justify="space-between" align="middle">
          <Col xs={24} sm={12} md={16}>
            <Search
              placeholder="Search notifications by title or description"
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/notification-create')}
              size="large"
              block
            >
              Create New Notification
            </Button>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          dataSource={filteredNotifications}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} notifications`,
          }}
        />
      </Card>
    </div>
  );
}

export default Notifications;

